import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import TajaNewsEnquiryService from "./Locality/Service/tajaNewsEnquiryService";

const useStyles = makeStyles((theme) => ({
  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(14px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  "@keyframes subtlePulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.25)" },
    "100%": { boxShadow: "0 0 0 10px rgba(25, 118, 210, 0)" },
  },
  page: {
    minHeight: "100vh",
    background: "#f6f7fb",
    padding: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4),
    },
  },
  shell: {
    maxWidth: 720,
    margin: "0 auto",
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: 14,
    boxShadow: "0 10px 30px rgba(18, 38, 63, 0.08)",
    marginTop: theme.spacing(2),
    transition: "transform 220ms ease, box-shadow 220ms ease",
    animation: "$fadeInUp 320ms ease both",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 14px 34px rgba(18, 38, 63, 0.12)",
    },
  },
  infoCard: {
    background: "#ffffff",
    animationDelay: "40ms",
  },
  formCard: {
    background: "#fbfcff",
    border: "1px solid #e8edf6",
    animationDelay: "120ms",
  },
  centerIntro: {
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 800,
  },
  subtitle: {
    color: "rgba(0,0,0,0.65)",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
  },
  infoTitle: {
    fontWeight: 800,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  infoRow: {
    marginBottom: theme.spacing(1),
    fontSize: "0.95rem",
    lineHeight: 1.5,
  },
  dot: {
    color: "#d32f2f",
    marginRight: theme.spacing(1),
    fontWeight: 800,
  },
  dateChip: {
    display: "inline-block",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
    padding: "4px 10px",
    borderRadius: 999,
    background: "rgba(25, 118, 210, 0.12)",
    color: "#0d47a1",
    fontWeight: 700,
    fontSize: 12,
    transition: "transform 180ms ease, background-color 180ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      background: "rgba(25, 118, 210, 0.18)",
    },
  },
  fieldGap: {
    marginTop: theme.spacing(1),
  },
  formTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  helperText: {
    color: "rgba(0,0,0,0.6)",
    marginBottom: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  primaryButton: {
    borderRadius: 12,
    height: 44,
    fontWeight: 800,
    minWidth: 150,
    transition: "transform 160ms ease, box-shadow 160ms ease, filter 160ms ease",
    boxShadow: "0 6px 16px rgba(25, 118, 210, 0.28)",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 20px rgba(25, 118, 210, 0.35)",
      filter: "brightness(1.02)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
    "&:disabled": {
      boxShadow: "none",
    },
  },
  submittingButton: {
    animation: "$subtlePulse 1.2s ease infinite",
  },
  danger: {
    color: theme.palette.error.main,
    fontWeight: 700,
    marginTop: theme.spacing(1),
  },
  note: {
    color: "rgba(0,0,0,0.55)",
    fontSize: "0.85rem",
  },
}));

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatDDMMYYYY(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

export default function TajaNewsEnquiryPage() {
  const classes = useStyles();
  const dateText = useMemo(() => formatDDMMYYYY(new Date()), []);

  const [values, setValues] = useState({
    registeredEmailAddress: "",
    reasonForDeletion: "",
  });
  const [touched, setTouched] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errors = useMemo(() => {
    const e = {};
    if (!values.registeredEmailAddress.trim()) e.registeredEmailAddress = "Registered email address is required";
    else if (!emailRegex.test(values.registeredEmailAddress.trim())) e.registeredEmailAddress = "Enter a valid email";
    return e;
  }, [values.registeredEmailAddress]);

  const handleChange = (key) => (event) => {
    const next = event?.target?.value || "";
    setSubmitError("");
    setSubmitSuccess("");
    setValues((prev) => ({ ...prev, [key]: next }));
  };

  useEffect(() => {
    if (!submitSuccess) return undefined;
    const t = setTimeout(() => setSubmitSuccess(""), 2500);
    return () => clearTimeout(t);
  }, [submitSuccess]);

  const onSubmit = async () => {
    setTouched({ registeredEmailAddress: true });
    setSubmitError("");
    setSubmitSuccess("");

    if (Object.keys(errors).length > 0) {
      setSubmitError("Please fill required fields.");
      return;
    }

    try {
      setIsSubmitting(true);
      await TajaNewsEnquiryService.submitPublic({
        registeredEmailAddress: values.registeredEmailAddress.trim(),
        reasonForDeletion: values.reasonForDeletion.trim(),
      });
      setSubmitSuccess("Enquiry submitted successfully.");
      setValues({ registeredEmailAddress: "", reasonForDeletion: "" });
      setTouched({});
    } catch (err) {
      setSubmitError(err?.message || "Failed to submit enquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.shell}>
       

        <Card className={`${classes.card} ${classes.infoCard}`}>
          <div className={classes.centerIntro}>
            <Typography variant="h5" className={classes.title}>
              Delete Account
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
              Montestory — Account Deletion Request
            </Typography>
            <div className={classes.dateChip}>Today: {dateText}</div>
          </div>

          <Typography className={classes.infoTitle}>How to request account deletion</Typography>
          <Typography className={classes.infoRow}>
            <span className={classes.dot}>●</span>
            Fill out the form below with your registered email address
          </Typography>
          <Typography className={classes.infoRow}>
            <span className={classes.dot}>●</span>
            Or send a deletion request email to amar@srshta.com with your registered email ID
          </Typography>
        </Card>

        <Card className={`${classes.card} ${classes.formCard}`}>
          <Typography variant="h6" className={classes.formTitle}>
            Submit your request
          </Typography>
          <Typography variant="body2" className={classes.helperText}>
            Enter your registered email to continue.
          </Typography>

          <TextField
            fullWidth
            label="Registered email address *"
            value={values.registeredEmailAddress}
            onChange={handleChange("registeredEmailAddress")}
            onBlur={() => setTouched((t) => ({ ...t, registeredEmailAddress: true }))}
            error={Boolean(touched.registeredEmailAddress && errors.registeredEmailAddress)}
            helperText={
              touched.registeredEmailAddress && errors.registeredEmailAddress ? errors.registeredEmailAddress : " "
            }
            variant="outlined"
            autoComplete="off"
          />

          <TextField
            className={classes.fieldGap}
            fullWidth
            label="Reason for deletion (optional)"
            value={values.reasonForDeletion}
            onChange={handleChange("reasonForDeletion")}
            variant="outlined"
            multiline
            rows={4}
            autoComplete="off"
          />

          {submitError ? (
            <Typography variant="body2" className={classes.danger}>
              {submitError}
            </Typography>
          ) : null}

          <Box className={classes.actions}>

            <Button
              className={`${classes.primaryButton} ${isSubmitting ? classes.submittingButton : ""}`}
              variant="contained"
              color="primary"
              onClick={onSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <CircularProgress size={18} style={{ color: "white" }} />
                  Submitting...
                </Box>
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        </Card>
      </div>

      <Snackbar
        open={Boolean(submitSuccess)}
        autoHideDuration={2500}
        onClose={() => setSubmitSuccess("")}
        message={submitSuccess}
      />
    </div>
  );
}
